/**
 * OIDC configuration for both providers.
 *
 * Two identity providers, and almost everything below is the MIKE Cloud half. That is
 * not an accident of ordering: MIKE Cloud needs the scope forced onto every refresh and
 * the token narrowed after login, and DHI's Entra tenant needs neither, because it
 * honours the scope on the authorization-code exchange and returns exactly what was
 * asked for. Measured 2026-08-31 against a real token: `scp` came back as
 * `access_as_user` alone. So the branches below are narrow on purpose, and the Entra
 * path is the plain one.
 *
 * Three decisions here are measured rather than assumed, and each is invisible when
 * wrong, so they are written down next to the code that depends on them.
 *
 * **The service worker stays on.** It exists to keep tokens out of JavaScript, and it
 * was nearly disabled over a fear that it would buffer our server-sent-events streams.
 * Measured on 2026-08-25 against the library's real interception path: six events sent
 * 400ms apart arrived at 4/404/804/1204/1605/2006ms through the worker, against
 * 4/403/804/1204/1605/2006ms without it, with the server confirming the worker had
 * swapped the Authorization header. Identical. So we keep the worker and JavaScript
 * never holds a usable token: `accessToken` below is a placeholder the worker
 * substitutes on its way out.
 *
 * **`storage` is memory, and it is only the fallback.** With the worker off, the library
 * defaults to `sessionStorage`, which is XSS-readable and survives a reload. Since a
 * MIKE Cloud token is a platform credential rather than a session cookie for our app
 * alone, the fallback keeps tokens in memory instead. The cost is that a browser without
 * service workers re-authenticates on every reload; the IdP session cookie makes that a
 * redirect rather than a login. `login_state_storage` must stay `sessionStorage`
 * regardless: PKCE state has to survive the redirect to the IdP, and it is not a
 * credential.
 *
 * **`scope` has to be forced onto every refresh, and no configuration option does it.**
 * The platform returns all 38 platform scopes unless the token request names a scope,
 * and the library's refresh body carries none. `token_request_extras` is not the answer:
 * it is merged into the authorization-code exchange too, which rejects a `scope`
 * parameter outright (OpenIddict ID2074). So the wrapper below edits the refresh body
 * and nothing else.
 */

import type { OidcConfiguration } from '@axa-fr/react-oidc';
import type { AuthProviderConfig } from '../types/api';
import { getActiveProvider } from './config';
import { sameAccount } from './requestedAccount';

/** Provider names, which several branches below key on. */
export const MIKE_CLOUD = 'mike_cloud';
export const DHI_ENTRA = 'dhi_entra';

/**
 * The two scopes we want from MIKE Cloud, and the reason half of this file exists.
 *
 * A token with just these still carries `email`, `TenantId` and our client id in `aud`,
 * which is everything the backend validates, while being 401 on every platform endpoint.
 */
export const REQUESTED_SCOPE = 'openid offline_access';

/**
 * What we ask DHI's Entra tenant for.
 *
 * The API scope is what makes the access token ours: request only `openid profile email`
 * and Entra returns a token whose audience is Microsoft Graph, which our backend must
 * never accept as its own. The scope string is built from the client id because the
 * registration exposes its API as `api://<client id>`, so the two cannot drift.
 */
export function entraScope(clientId: string): string {
  return `openid profile email offline_access api://${clientId}/access_as_user`;
}

/** What to ask the given provider for. */
export function requestedScope(provider: AuthProviderConfig): string {
  return provider.name === DHI_ENTRA ? entraScope(provider.client_id) : REQUESTED_SCOPE;
}

/**
 * Whether a login against this provider hands back a token wider than we asked for.
 *
 * True for MIKE Cloud only, where the authorization-code exchange rejects a `scope`
 * parameter outright and returns all 38 platform scopes regardless. Stated as a
 * property of the provider rather than inferred from counting scopes, because the count
 * that would distinguish them today is a coincidence of two numbers and not a rule.
 */
export function narrowsScopesAfterLogin(provider: AuthProviderConfig): boolean {
  return provider.name === MIKE_CLOUD;
}

/**
 * Extra authorize parameters for a login, given the address typed on the email step.
 *
 * Entra only. The email step collects an address and then the redirect used to drop it,
 * so a colleague typed it twice: once for us, once for Microsoft. Entra honours
 * `login_hint`, which pre-fills or skips its account picker, and `domain_hint`, which
 * skips home-realm discovery, so a colleague lands on the DHI password page directly. The
 * domain comes from the address when there is one and from the provider's first claimed
 * domain otherwise, so a welcome link with no address still skips a screen.
 *
 * `prompt=select_account` is the one that makes the typed address mean something. A hint
 * on its own does not interrupt single sign-on: Entra answers from its own cookie, so
 * typing a colleague's address signed you straight back in as yourself. Forcing the picker
 * is what makes it ask, and it is asked for only when the address is not the account we
 * remember, or when we remember nobody. That keeps the extra screen off the ordinary path,
 * where a returning colleague types their own address and expects the one click they have
 * always had, and puts it exactly on the case that was broken. Forcing the picker is also
 * what makes Entra ignore the `login_hint` beside it, so the address is chosen rather than
 * pre-filled; the hint stays because the two travel together on every login without one.
 *
 * Remembering nobody forces the picker even without an address, on a welcome link or the
 * `/signin/dhi_entra` path. A sign-out forgets the account and leaves Microsoft's session
 * alive (see `signOutEndsPlatformSession`), so without the picker the next sign-in on
 * those paths would be the same account again in silence, which reads as the sign-out not
 * having worked. The cost is one click on a first sign-in by link.
 *
 * MIKE Cloud gets nothing, and not because it would object: measured 2026-08-26, its
 * `/authorize` accepts `login_hint`, `prompt=login` and `prompt=select_account` and
 * forwards none of them to Entra, where the credential check actually happens. So nothing
 * sent from here can make it ask who you are, and the account that comes back is checked
 * against the typed address afterwards instead. See `auth/requestedAccount.ts`.
 *
 * Only plain keys, on purpose. The library sends a login extra to the token endpoint as
 * well only when its key ends in `:token_request`, so these reach the authorize request
 * and nothing else.
 */
export function authorizeExtras(
  provider: AuthProviderConfig,
  loginHint: string | null,
  rememberedAccount: string | null = null,
): Record<string, string> | undefined {
  if (provider.name !== DHI_ENTRA) return undefined;
  const extras: Record<string, string> = {};
  const hint = loginHint?.trim();
  if (hint) extras.login_hint = hint;
  const at = hint?.lastIndexOf('@') ?? -1;
  const domain = at > -1 ? hint?.slice(at + 1) : provider.email_domains?.[0];
  if (domain) extras.domain_hint = domain;
  if (!rememberedAccount || (hint && !sameAccount(hint, rememberedAccount))) {
    extras.prompt = 'select_account';
  }
  return Object.keys(extras).length > 0 ? extras : undefined;
}

const BASE = import.meta.env.BASE_URL;

/**
 * Registered redirect URIs.
 *
 * The platform validates the *path* and ignores the port, so these are derived from the
 * deployment's base path rather than hardcoded: served under `/apps/mikeai/`, the
 * callback becomes `/apps/mikeai/authentication/callback` and that exact path has to be
 * registered, or the login fails with `invalid_client` before the user sees anything.
 */
export const CALLBACK_PATH = `${BASE}authentication/callback`;
export const SILENT_CALLBACK_PATH = `${BASE}authentication/silent-callback`;

/**
 * A `Storage` that keeps everything in memory and never touches the disk.
 *
 * A Proxy because the library reaches into the store both ways: `getItem`/`setItem` in
 * places, plain property access (`storage['oidc.default']`) in others. One backing Map
 * behind both is what keeps a token from being written through one door and read as
 * undefined through the other.
 */
export function createMemoryStorage(): Storage {
  const data = new Map<string, string>();
  const api = {
    get length() {
      return data.size;
    },
    key: (index: number) => Array.from(data.keys())[index] ?? null,
    getItem: (key: string) => data.get(key) ?? null,
    setItem: (key: string, value: string) => {
      data.set(key, String(value));
    },
    removeItem: (key: string) => {
      data.delete(key);
    },
    clear: () => data.clear(),
  };

  return new Proxy(api, {
    get: (target, prop) => {
      if (prop in target) return target[prop as keyof typeof target];
      return typeof prop === 'string' ? data.get(prop) : undefined;
    },
    set: (_target, prop, value) => {
      if (typeof prop === 'string') data.set(prop, String(value));
      return true;
    },
    deleteProperty: (_target, prop) => {
      if (typeof prop === 'string') data.delete(prop);
      return true;
    },
    has: (target, prop) => prop in target || (typeof prop === 'string' && data.has(prop)),
  }) as unknown as Storage;
}

/**
 * Add `scope` to a refresh-grant token request body, and leave every other body alone.
 *
 * Edits the encoded string rather than round-tripping it through `URLSearchParams`,
 * because with the service worker on, this body carries a placeholder the worker finds
 * by substring match, and re-encoding is a way to break that from a distance.
 */
export function withRefreshScope(body: string, scope: string = REQUESTED_SCOPE): string {
  if (!/(^|&)grant_type=refresh_token(&|$)/.test(body)) return body;
  if (/(^|&)scope=/.test(body)) return body;
  // `+` rather than `%20` for the separating space. Both are legal in a form-encoded
  // body and every server decodes both, but `+` is the form: it is what the probes that
  // measured this platform honouring an explicit scope actually sent, and this is not a
  // place to substitute an equivalent nobody has tried.
  const encoded = scope.split(' ').map(encodeURIComponent).join('+');
  return `${body}&scope=${encoded}`;
}

/**
 * Wrap `fetch` so every MIKE Cloud refresh names its scopes.
 *
 * MIKE Cloud only, and the check is not decoration. This wrapper is built once at module
 * scope, before anyone has chosen a provider, so without it every refresh would have
 * MIKE Cloud's two scopes appended, Entra included. Entra honours what it is sent, so the
 * renewed token would come back without `api://<client id>/access_as_user` and therefore
 * with the wrong audience, and the backend would refuse it. Nothing would go wrong at
 * login: the first token is correct and only its replacement is not, so the session would
 * work for an hour and then start answering 401.
 *
 * The provider is read per call rather than captured, because it is chosen after this is
 * built.
 */
export function createOidcFetch(baseFetch: typeof fetch): typeof fetch {
  return (input, init) => {
    const provider = getActiveProvider();
    if (
      provider?.name === MIKE_CLOUD &&
      init?.method?.toUpperCase() === 'POST' &&
      typeof init.body === 'string'
    ) {
      const body = withRefreshScope(init.body);
      if (body !== init.body) return baseFetch(input, { ...init, body });
    }
    return baseFetch(input, init);
  };
}

/** Number of scopes a correctly narrowed token carries, asserted at runtime after login. */
export const EXPECTED_SCOPE_COUNT = REQUESTED_SCOPE.split(' ').length;

/** Count the scopes in a decoded access-token payload; -1 when there is no scope claim. */
export function countScopes(accessTokenPayload: unknown): number {
  const scope = (accessTokenPayload as { scope?: unknown } | null | undefined)?.scope;
  if (typeof scope === 'string') return scope.split(' ').filter(Boolean).length;
  if (Array.isArray(scope)) return scope.length;
  return -1;
}

/**
 * Built from what the backend reports, not from build args.
 *
 * The three values used to be `VITE_MIKE_CLOUD_*`, compiled into the bundle. That made
 * an image built without them into a sign-in screen that could never sign anyone in,
 * and it made the SPA's idea of the auth mode a second setting that had to agree with
 * the backend's `AUTH_PROVIDER` by hand. They are now one value, read at boot from
 * `/api/v1/auth/config`, so the two halves cannot disagree.
 */
export function buildOidcConfiguration(
  provider: AuthProviderConfig,
  origin: string = window.location.origin,
): OidcConfiguration {
  return {
    authority: provider.authority,
    client_id: provider.client_id,
    redirect_uri: `${origin}${CALLBACK_PATH}`,
    silent_redirect_uri: `${origin}${SILENT_CALLBACK_PATH}`,
    scope: requestedScope(provider),
    // MIKE Cloud only. `tenantId` scopes the login to our site and is what puts TenantId
    // in the resulting token, which is the claim the backend checks; without it the token
    // is useless to us. Entra needs no equivalent, because its tenant is already in the
    // authority, and sending an unknown parameter there is a way to find out that some
    // providers reject what they do not recognise.
    ...(provider.name === MIKE_CLOUD ? { extras: { tenantId: provider.tenant_id } } : {}),
    // Plural "tokens" is the library's spelling. The singular reads just as naturally and
    // is silently ignored, which is a slow way to discover that renewal never runs.
    refresh_time_before_tokens_expiration_in_second: 120,
    service_worker_relative_url: `${BASE}OidcServiceWorker.js`,
    storage: createMemoryStorage(),
    login_state_storage: sessionStorage,
  };
}

/**
 * Whether signing out also ends the identity provider's session in this browser.
 *
 * Not for Entra. A colleague's Microsoft session is the one they work in all day, and
 * ending it signed them out of Outlook and the portal as well, while a DHI-managed device
 * signed them straight back in through Windows anyway. So an Entra sign-out clears only
 * ours, and the next sign-in asks Microsoft for its account picker instead, which is what
 * keeps switching accounts possible. MIKE Cloud forwards no `prompt` to Microsoft, so
 * ending its session is the only way its next sign-in can be a different account. See
 * adr/0038.
 */
export function signOutEndsPlatformSession(provider: AuthProviderConfig): boolean {
  return provider.name !== DHI_ENTRA;
}

/**
 * Whether this provider's end-session endpoint can be used in the current tab.
 *
 * MIKE Cloud honours `post_logout_redirect_uri` only alongside an
 * `id_token_hint`, and the id token is at hand: the service worker replaces the access and
 * refresh tokens with placeholders and passes the id token through untouched (the earlier
 * belief that it hid all three was never measured; this was, on 2026-09-03, and the round
 * trip came back to `/?state=probe`). Without an id token, which a session never lacks in
 * practice, the fallback is the second tab.
 */
export function endsSessionInPlace(
  provider: AuthProviderConfig,
  idToken: string | null = null,
): boolean {
  return provider.name === MIKE_CLOUD && !!idToken;
}

/**
 * Where an in-place sign-out returns to: the app root, which has to be accepted by the
 * platform as a post-logout redirect. Boot at the root lands on the email step.
 */
export function signOutReturnUrl(origin: string = window.location.origin): string {
  return `${origin}${BASE}`;
}

/**
 * The provider's end-session endpoint, which is the only thing that ends the session
 * behind ours.
 *
 * Built by hand rather than through the library's `logoutAsync`, which sends the worker's
 * placeholder as the hint and so never closes the loop.
 *
 * MIKE Cloud gets `id_token_hint` and `post_logout_redirect_uri` when an id token is
 * given, which is what makes it return to us (measured 2026-09-03; without the hint the
 * redirect is ignored, measured 2026-08-26). It forwards no `logout_hint` to Microsoft,
 * so its chain still shows Microsoft's "which account" picker for one click. Without an
 * id token it gets the bare URL, for the second-tab fallback.
 */
export function platformSignOutUrl(
  provider: AuthProviderConfig | null = getActiveProvider(),
  {
    returnTo = null,
    idTokenHint = null,
  }: { returnTo?: string | null; idTokenHint?: string | null } = {},
): string {
  if (!provider || !signOutEndsPlatformSession(provider)) return '';
  const base = provider.authority.endsWith('/')
    ? provider.authority.slice(0, -1)
    : provider.authority;
  const params = new URLSearchParams();
  if (idTokenHint) {
    params.set('id_token_hint', idTokenHint);
    if (returnTo) params.set('post_logout_redirect_uri', returnTo);
  }
  const query = params.toString();
  return query ? `${base}/logout?${query}` : `${base}/logout`;
}

/**
 * The signed-in email address, from a decoded token payload.
 *
 * One chain rather than one per provider, because the claims do not collide: MIKE Cloud
 * sends `email` and `upn`, Entra sends `preferred_username`. Order matters only in that
 * it matches what each backend provider derives identity from, so the address this
 * screen shows is the one the account is actually keyed on.
 */
export function emailFromPayload(payload: unknown): string | null {
  const claims = payload as
    | { email?: unknown; upn?: unknown; preferred_username?: unknown }
    | null
    | undefined;
  for (const value of [claims?.email, claims?.upn, claims?.preferred_username]) {
    if (typeof value === 'string' && value) return value;
  }
  return null;
}
